import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { StaticOneTrustIcon } from './StaticOneTrustIcon';
import { GlobalPrivacyRightsModal } from './GlobalPrivacyRightsModal';

export function PrivacyRightsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState('connecticut');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const privacyForms = [
    {
      id: 'gdpr',
      title: "GDPR Data Subject Rights",
      description: "Manage data subject access requests (DSARs) for EU/EEA residents with automated verification and response tracking",
      link: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/36571b86-38f8-416a-93f1-ecbd1630311a"
    },
    {
      id: 'ccpa',
      title: "CCPA California Privacy Rights",
      description: "Handle California consumer privacy requests with built-in verification and response timelines",
      link: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/fe1afed8-a8d1-4cc4-876b-66fb542a8a24"
    },
    {
      id: 'pipeda',
      title: "PIPEDA Privacy Rights",
      description: "Process Canadian privacy rights requests with automated workflows and compliance documentation",
      link: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/497ca086-bdc7-4ec6-9e5c-d8b912303d66"
    },
    {
      id: 'global',
      title: "Global Privacy Rights",
      description: "Manage privacy rights requests from anywhere with a flexible, customizable workflow",
      link: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/1683554e-8ba8-41ee-9bf4-3621645091cd"
    }
  ];

  const otherPrivacyForms = {
    connecticut: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/fcd29e04-8792-4cee-b6a7-7331eb61c9fb",
    colorado: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/b3eb7bf9-5985-4a82-806b-0c625ae6e740",
    china: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/f10d0cd8-b7a7-4663-b72f-9a9fda7d52a7",
    brazil: "https://fllr-privacy.my.onetrust.com/webform/73cb21c4-9737-45b0-ba87-528432c7bbca/5bc323b1-fbe9-4884-a150-59fc2c24cd18"
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/privacy-center"
            className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Privacy Center
          </Link>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-12">
          <div className="flex items-center gap-4 mb-6">
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8]">
              Privacy Rights
            </h1>
            <button
              onClick={() => setIsModalOpen(true)}
              className="relative group"
              aria-label="Learn more about OneTrust privacy rights"
            >
              <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
              <StaticOneTrustIcon size="lg" />
            </button>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Select the appropriate form based on your location to exercise your privacy rights. This is a test website demonstrating how 
            easily you could implement privacy rights management for your own website using OneTrust's hosted webforms.
          </p>

          <div className="grid gap-8">
            {privacyForms.map((form) => (
              <div 
                key={form.id}
                className="bg-gray-50 dark:bg-gray-700 p-8 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {form.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {form.description}
                </p>
                <a
                  href={form.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-[#5e9bb8] hover:bg-[#4d8299] text-white rounded-xl font-medium transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Access Form
                </a>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
            <h2 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-6">
              Other Privacy Rights Forms
            </h2>
            <div className="space-y-6">
              <select
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-[#5e9bb8] focus:border-[#5e9bb8] transition-all duration-300"
              >
                <option value="connecticut">Connecticut Privacy Rights</option>
                <option value="colorado">Colorado Privacy Rights</option>
                <option value="china">China Privacy Rights</option>
                <option value="brazil">Brazil Privacy Rights</option>
              </select>

              <div className="ot-form-wrapper border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
                <iframe
                  src={otherPrivacyForms[selectedForm]}
                  title="Privacy Rights Form"
                  className="w-full h-[700px] border-0"
                />
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-600">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Important Note
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                You may be required to verify your identity before we can fulfill your request. 
                This helps us ensure the security of your personal information.
              </p>
            </div>
          </div>
        </div>
      </div>

      <GlobalPrivacyRightsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}